import { LockKeyhole } from "lucide-react";
import React from "react";
import { Link, useLocation } from "react-router-dom";

import { IgTooltip, IgTypography } from "@intelligems/ig-theme";
import {
  Box,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { MainRoute } from "@src/types/navigation";

import { activeRoute } from "../../Utils/helpers";

const PREFIX = "IgListItemLink";

const classes = {
  listItem: `${PREFIX}-listItem`,
};

const StyledListItem = styled(ListItem)<{ $collapsed?: boolean }>(
  ({ theme, $collapsed }) => ({
    marginBottom: theme.spacing(1),
    "& .MuiListItemButton-root.Mui-selected": {
      backgroundColor: theme.igBackgroundColors.interactive.neutral.active,
    },
    "& .MuiListItemButton-root": {
      borderRadius: "8px",
      height: "32px",
      color: theme.igBackgroundColors.interactive.neutral.focused,
      ...($collapsed && {
        display: "flex !important",
        justifyContent: "center !important",
        alignItems: "center !important",
        padding: "0 !important",
      }),
    },
    "& .MuiListItemIcon-root": {
      paddingRight: theme.spacing(2),
      minWidth: "16px",
      ...($collapsed && {
        padding: "0 !important",
        justifyContent: "center !important",
        display: "flex !important",
      }),
    },
    "& .MuiTouchRipple-child": {
      backgroundColor: theme.igBackgroundColors.interactive.neutral.focused,
    },
    [`& .${classes.listItem}`]: {
      borderRadius: "8px",
      color: "purple",
      "&:hover": {
        backgroundColor: theme.igBackgroundColors.interactive.neutral.hover,
      },
    },
  })
);

interface ListItemLinkProps {
  handleClick?: (event?: any) => void;
  drawerOpen: boolean;
  Route: Pick<MainRoute, "icon" | "sidebarName" | "path">;
  exact?: boolean;
  hasAccess: boolean;
  isLink?: boolean;
  isSubRoute?: boolean;
  chip?: React.ReactNode;
}

export const IgListItemLink = ({
  handleClick,
  Route,
  exact,
  drawerOpen,
  hasAccess,
  isLink = true,
  isSubRoute = false,
  chip,
}: ListItemLinkProps) => {
  const { pathname } = useLocation();
  const theme = useTheme();
  const isAdmin = useIsAdmin();

  const getTooltipContent = () => {
    if (drawerOpen) return "";

    let shortcut = "";
    if (isAdmin) {
      if (Route.sidebarName === "Tests") shortcut = "Ctrl+D";
      else if (Route.sidebarName === "Personalizations") shortcut = "Ctrl+X";
    }

    if (shortcut) {
      return (
        <IgTypography variant={"body2"}>
          {Route.sidebarName}{" "}
          <IgTypography
            sx={{ border: ".5px solid white", borderRadius: "2px" }}
            variant={"body2"}
          >
            {shortcut}
          </IgTypography>
        </IgTypography>
      );
    }

    return Route.sidebarName;
  };

  const content = () => {
    return (
      <IgTooltip placement={"right"} title={getTooltipContent()}>
        <StyledListItem
          disablePadding
          className={classes.listItem}
          $collapsed={!drawerOpen}
        >
          <ListItemButton
            selected={activeRoute(Route.path, pathname, exact)}
            onClick={(e) => handleClick && handleClick(e)}
          >
            {Route.icon && (isSubRoute ? !drawerOpen : true) && (
              <ListItemIcon sx={{ color: theme.igIconColors.light }}>
                {hasAccess ? Route.icon : <LockKeyhole size={18} />}
              </ListItemIcon>
            )}
            {drawerOpen ? (
              <ListItemText
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                }}
                primary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {Route.sidebarName}
                    {chip}
                  </Box>
                }
              />
            ) : null}
          </ListItemButton>
        </StyledListItem>
      </IgTooltip>
    );
  };

  if (!isLink) return content();
  return (
    <Link
      key={Route.path}
      style={{ textDecoration: "none" }}
      to={hasAccess ? Route.path : window.location.pathname}
      state={hasAccess ? { name: Route.sidebarName.toLowerCase() } : {}}
    >
      {content()}
    </Link>
  );
};
